import React, { useState, useMemo } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAdminOverviewStatsQuery,
  useGetAdminLiveSummaryQuery,
  useGetAdminRecentActivityQuery,
} from "../../app/services/meseeknaApiV2"
import { HiOutlineOfficeBuilding, HiOutlineUserGroup, HiOutlineX } from "react-icons/hi"
import { BiPulse, BiCheckCircle } from "react-icons/bi"
import { FiUsers, FiActivity } from "react-icons/fi"
import { MdOutlineApproval, MdOutlineSubscriptions } from "react-icons/md"

// Icon wrapper components to fix TypeScript compatibility with react-icons
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const OfficeBuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserGroupIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineUserGroup {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CloseIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineX {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const PulseIcon = (props: { size?: number; className?: string }): JSX.Element => <BiPulse {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckCircleIcon = (props: { size?: number; className?: string }): JSX.Element => <BiCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UsersIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUsers {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ActivityIcon = (props: { size?: number; className?: string }): JSX.Element => <FiActivity {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ApprovalIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineApproval {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SubscriptionsIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineSubscriptions {...props} />

const StatCard: React.FC<{
  title: string
  value: number
  icon: React.ReactNode
  color: string
  description?: string
}> = ({ title, value, icon, color, description }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {description && (
          <p className="text-xs text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <div className={`p-3 rounded-full ${color}`}>{icon}</div>
    </div>
  </div>
)

const LiveStatCard: React.FC<{
  title: string
  value: number
  icon: React.ReactNode
  isActive?: boolean
  onClick?: () => void
  clickable?: boolean
}> = ({ title, value, icon, isActive, onClick, clickable }) => (
  <div
    onClick={clickable ? onClick : undefined}
    className={`rounded-lg border p-4 ${isActive && value > 0 ? "bg-green-50 border-green-200" : "bg-white border-gray-200"} ${clickable ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
  >
    <div className="flex items-center gap-3">
      <div
        className={`p-2 rounded-lg ${isActive && value > 0 ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}
      >
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{title}</p>
      </div>
      {isActive && value > 0 && (
        <div className="ml-auto">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
            Live
          </span>
        </div>
      )}
      {clickable && (
        <div className="ml-auto">
          <span className="text-xs text-blue-600 font-medium">View</span>
        </div>
      )}
    </div>
  </div>
)

const ActivityItem: React.FC<{
  type: string
  userName?: string
  organizationName?: string
  simulationName?: string
  timestamp: string
}> = ({ type, userName, organizationName, simulationName, timestamp }) => {
  const getActivityIcon = () => {
    switch (type) {
      case "simulation_started":
        return (
          <div className="p-2 bg-blue-100 text-blue-600 rounded-full">
            <ActivityIcon size={16} />
          </div>
        )
      case "simulation_completed":
        return (
          <div className="p-2 bg-green-100 text-green-600 rounded-full">
            <CheckCircleIcon size={16} />
          </div>
        )
      case "team_session_created":
        return (
          <div className="p-2 bg-purple-100 text-purple-600 rounded-full">
            <UserGroupIcon size={16} />
          </div>
        )
      case "team_session_completed":
        return (
          <div className="p-2 bg-emerald-100 text-emerald-600 rounded-full">
            <UserGroupIcon size={16} />
          </div>
        )
      default:
        return (
          <div className="p-2 bg-gray-100 text-gray-600 rounded-full">
            <ActivityIcon size={16} />
          </div>
        )
    }
  }

  const getActivityText = () => {
    switch (type) {
      case "simulation_started":
        return (
          <>
            <span className="font-medium">{userName || "User"}</span> started{" "}
            <span className="font-medium">{simulationName || "a simulation"}</span>
          </>
        )
      case "simulation_completed":
        return (
          <>
            <span className="font-medium">{userName || "User"}</span> completed{" "}
            <span className="font-medium">{simulationName || "a simulation"}</span>
          </>
        )
      case "team_session_created":
        return (
          <>
            <span className="font-medium">{userName || "User"}</span> created a team
            session
          </>
        )
      case "team_session_completed":
        return (
          <>
            Team session completed by{" "}
            <span className="font-medium">{organizationName || "organization"}</span>
          </>
        )
      default:
        return "Activity"
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)

    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0">
      {getActivityIcon()}
      <div className="flex-1">
        <p className="text-sm text-gray-700">{getActivityText()}</p>
        {organizationName && (
          <p className="text-xs text-gray-400">{organizationName}</p>
        )}
      </div>
      <span className="text-xs text-gray-400">{formatTime(timestamp)}</span>
    </div>
  )
}

const SuperAdminOverview: React.FC = () => {
  const [showCompletedModal, setShowCompletedModal] = useState(false)

  const { data: overviewData, isLoading: overviewLoading } =
    useGetAdminOverviewStatsQuery()
  const { data: liveData, isLoading: liveLoading } =
    useGetAdminLiveSummaryQuery(undefined, {
      pollingInterval: 30000, // Refresh every 30 seconds
    })
  const { data: activityData, isLoading: activityLoading } =
    useGetAdminRecentActivityQuery({ limit: 100 }, {
      pollingInterval: 60000, // Refresh every 60 seconds
    })

  const stats = overviewData?.data
  const live = liveData?.data
  const activities = activityData?.data || []

  // Filter activities to show only completions from today
  const todayCompletions = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return activities.filter((activity) => {
      if (activity.type !== "simulation_completed") return false
      const activityDate = new Date(activity.timestamp)
      activityDate.setHours(0, 0, 0, 0)
      return activityDate.getTime() === today.getTime()
    })
  }, [activities])

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900">
                Platform Overview
              </h1>
              <p className="text-gray-500 mt-1">
                Monitor platform health and key metrics
              </p>
            </div>

            {/* Live Activity Section */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PulseIcon className="text-green-500" />
                Live Activity
              </h2>
              {liveLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse"
                    >
                      <div className="h-16 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <LiveStatCard
                    title="Individual Simulations"
                    value={live?.individualActive || 0}
                    icon={<UsersIcon size={20} />}
                    isActive
                  />
                  <LiveStatCard
                    title="Team Sessions"
                    value={live?.teamSessionsActive || 0}
                    icon={<UserGroupIcon size={20} />}
                    isActive
                  />
                  <LiveStatCard
                    title="Collaboration Rooms"
                    value={live?.collaborationRoomsActive || 0}
                    icon={<ActivityIcon size={20} />}
                    isActive
                  />
                  <LiveStatCard
                    title="Completed Today"
                    value={live?.completedToday || 0}
                    icon={<CheckCircleIcon size={20} />}
                    clickable
                    onClick={() => setShowCompletedModal(true)}
                  />
                </div>
              )}
            </div>

            {/* Overview Stats */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Platform Statistics
              </h2>
              {overviewLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
                    >
                      <div className="h-20 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Total Organizations"
                    value={stats?.totalOrganizations || 0}
                    icon={<OfficeBuildingIcon size={24} className="text-blue-600" />}
                    color="bg-blue-100"
                  />
                  <StatCard
                    title="Active Subscriptions"
                    value={stats?.activeSubscriptions || 0}
                    icon={<SubscriptionsIcon size={24} className="text-green-600" />}
                    color="bg-green-100"
                  />
                  <StatCard
                    title="Total Users"
                    value={stats?.totalUsers || 0}
                    icon={<UsersIcon size={24} className="text-purple-600" />}
                    color="bg-purple-100"
                  />
                  <StatCard
                    title="Pending Approvals"
                    value={stats?.pendingApprovals || 0}
                    icon={<ApprovalIcon size={24} className="text-amber-600" />}
                    color="bg-amber-100"
                    description="Custom simulations"
                  />
                  <StatCard
                    title="Active Individual Sims"
                    value={stats?.activeIndividualSimulations || 0}
                    icon={<ActivityIcon size={24} className="text-cyan-600" />}
                    color="bg-cyan-100"
                  />
                  <StatCard
                    title="Active Team Sims"
                    value={stats?.activeTeamSimulations || 0}
                    icon={<UserGroupIcon size={24} className="text-indigo-600" />}
                    color="bg-indigo-100"
                  />
                  <StatCard
                    title="Completed Today"
                    value={stats?.completedToday || 0}
                    icon={<CheckCircleIcon size={24} className="text-emerald-600" />}
                    color="bg-emerald-100"
                  />
                </div>
              )}
            </div>

            {/* Recent Activity Feed */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Recent Activity
                </h2>
                <p className="text-sm text-gray-500">Last 24 hours</p>
              </div>
              <div className="p-4 max-h-96 overflow-y-auto">
                {activityLoading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center gap-3 animate-pulse">
                        <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2 mt-1"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : activities.length > 0 ? (
                  activities.slice(0, 20).map((activity) => (
                    <ActivityItem
                      key={activity.id}
                      type={activity.type}
                      userName={activity.userName}
                      organizationName={activity.organizationName}
                      simulationName={activity.simulationName}
                      timestamp={activity.timestamp}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ActivityIcon size={40} className="mx-auto text-gray-300 mb-2" />
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Completed Today Modal */}
      {showCompletedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Completed Today</h3>
                <p className="text-sm text-gray-500">{todayCompletions.length} simulation{todayCompletions.length !== 1 ? "s" : ""} completed</p>
              </div>
              <button
                onClick={() => setShowCompletedModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <CloseIcon size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {todayCompletions.length > 0 ? (
                <div className="space-y-3">
                  {todayCompletions.map((activity) => (
                    <div key={activity.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="p-2 bg-green-100 text-green-600 rounded-full">
                        <CheckCircleIcon size={16} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{activity.userName || "Unknown User"}</p>
                        <p className="text-xs text-gray-500">{activity.simulationName || "Unknown Simulation"}</p>
                        {activity.organizationName && (
                          <p className="text-xs text-gray-400">{activity.organizationName}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(activity.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircleIcon size={40} className="mx-auto text-gray-300 mb-2" />
                  <p>No completions recorded today yet</p>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowCompletedModal(false)}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SuperAdminOverview
